import React, { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { uploadImageToCloudinary } from "../../upload-image-config";
import StateSelector from "../Common/StateSelector";
import { authService } from "../../services/authService";

const RegisterAgent = () => {
  const navigate = useNavigate();
  const { registerAsAgent } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [message, setMessage] = useState("");

  const formatPhoneNumberForApi = (value = "") => {
    if (!value) return "";
    let digits = value.replace(/\D/g, "");

    if (digits.startsWith("234")) {
      digits = digits.slice(3);
    }

    if (digits.startsWith("0")) {
      digits = digits.slice(1);
    }

    digits = digits.slice(0, 10);

    if (digits.length !== 10) {
      return "";
    }

    return `234${digits}`;
  };

  const [formData, setFormData] = useState({
    verificationMethod: "",
    verificationNumber: "",
    agentImage: "",
    agent_name: "",
    state: "",
    localGovernmentAddress: "",
    streetAddress: "",
    phone_number: "",
    confirmationCode: "123456", // Default as specified
    openHours: {
      monday: { open: "", close: "" },
      tuesday: { open: "", close: "" },
      wednesday: { open: "", close: "" },
      thursday: { open: "", close: "" },
      friday: { open: "", close: "" },
      saturday: { open: "", close: "" },
      sunday: { open: "", close: "" },
    },
  });
  const [agentPhoneOtpSent, setAgentPhoneOtpSent] = useState(false);
  const [agentPhoneOtp, setAgentPhoneOtp] = useState("");
  const [agentPhoneOtpMessage, setAgentPhoneOtpMessage] = useState("");
  const [agentPhoneOtpError, setAgentPhoneOtpError] = useState("");
  const [agentPhoneOtpSending, setAgentPhoneOtpSending] = useState(false);
  const [agentPhoneOtpVerifying, setAgentPhoneOtpVerifying] = useState(false);
  const [agentPhoneVerificationToken, setAgentPhoneVerificationToken] =
    useState("");
  const [agentPhoneOtpVerified, setAgentPhoneOtpVerified] = useState(false);

  const handleChange = (e) => {
    if (e.target.name === "phone_number") {
      setAgentPhoneOtpSent(false);
      setAgentPhoneOtp("");
      setAgentPhoneOtpMessage("");
      setAgentPhoneOtpError("");
      setAgentPhoneOtpVerified(false);
      setAgentPhoneVerificationToken("");

      let digitsOnly = e.target.value.replace(/\D/g, "");

      if (digitsOnly.startsWith("234")) {
        digitsOnly = digitsOnly.slice(3);
      }

      if (digitsOnly.startsWith("0")) {
        digitsOnly = digitsOnly.slice(1);
      }

      digitsOnly = digitsOnly.slice(0, 10);

      setFormData((prev) => ({
        ...prev,
        phone_number: digitsOnly,
      }));
      return;
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(URL.createObjectURL(file));
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
    }
  };

  const handleOpenHoursChange = (day, field, value) => {
    setFormData({
      ...formData,
      openHours: {
        ...formData.openHours,
        [day]: {
          ...formData.openHours[day],
          [field]: value,
        },
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setAgentPhoneOtpError("");

    const sanitizedPhone = formatPhoneNumberForApi(formData.phone_number);

    if (!sanitizedPhone) {
      setAgentPhoneOtpError(
        "Please enter a valid 10-digit Nigerian phone number before registering."
      );
      setLoading(false);
      return;
    }

    if (!agentPhoneOtpVerified || !agentPhoneVerificationToken) {
      setAgentPhoneOtpError(
        "Please verify your phone number before submitting your agent application."
      );
      setLoading(false);
      return;
    }

    try {
      let imageUrl = formData.image;

      if (formData.image instanceof File) {
        imageUrl = await uploadImageToCloudinary(formData.image);
      }
      // Only send the fields the backend expects
      const payload = {
        verificationMethod: formData.verificationMethod,
        verificationNumber: formData.verificationNumber,
        agentImage: imageUrl,
        agent_name: formData.agent_name,
        phone_number: sanitizedPhone,
        localGovernmentAddress: formData.localGovernmentAddress,
        streetAddress: formData.streetAddress,
        openHours: formData.openHours,
        confirmationCode: formData.confirmationCode,
        agentPhoneVerificationToken,
      };
      await registerAsAgent(payload);
      toast.success(
        "Application submitted. Your request is under review — we will email you when approved."
      );
      setMessage("");
      setTimeout(() => {
        navigate("/");
      }, 1500);
    } catch (error) {
      console.error("Agent registration error:", error);
      toast.error(error.response?.data?.message || "Error submitting application");
      setMessage(error.response?.data?.message || "Error registering as agent");
    } finally {
      setLoading(false);
    }
  };

  const handleSendAgentPhoneOtp = async () => {
    const sanitizedPhone = formatPhoneNumberForApi(formData.phone_number);

    if (!sanitizedPhone) {
      setAgentPhoneOtpError("Please enter your 10-digit phone number to receive a code.");
      return;
    }

    setAgentPhoneOtpSending(true);
    setAgentPhoneOtpError("");
    setAgentPhoneOtpMessage("");
    setAgentPhoneOtp("");
    setAgentPhoneOtpVerified(false);
    setAgentPhoneVerificationToken("");

    try {
      const response = await authService.requestAgentPhoneVerificationOtp({
        phoneNumber: sanitizedPhone,
      });
      setAgentPhoneOtpSent(true);
      setAgentPhoneOtpMessage(
        response.message || "We sent a 6-digit code to your WhatsApp. If it does not arrive, check your text messages."
      );
    } catch (err) {
      const phoneInUse =
        err.response?.data?.code === "PHONE_NUMBER_IN_USE" ||
        err.response?.data?.message?.toLowerCase?.().includes("linked to another account");
      if (phoneInUse) {
        setAgentPhoneOtpSent(false);
        setAgentPhoneOtpVerified(false);
        setAgentPhoneVerificationToken("");
      }
      setAgentPhoneOtpError(
        err.response?.data?.message ||
          "We couldn't send the verification code. Please try again."
      );
    } finally {
      setAgentPhoneOtpSending(false);
    }
  };

  const handleVerifyAgentPhoneOtp = async () => {
    const sanitizedPhone = formatPhoneNumberForApi(formData.phone_number);

    if (!sanitizedPhone) {
      setAgentPhoneOtpError("Please enter your phone number to receive a code.");
      return;
    }

    if (agentPhoneOtp.length !== 6) {
      setAgentPhoneOtpError("Please enter the 6-digit verification code.");
      return;
    }

    setAgentPhoneOtpVerifying(true);
    setAgentPhoneOtpError("");
    setAgentPhoneOtpMessage("");

    try {
      const response = await authService.verifyAgentPhoneVerificationOtp({
        phoneNumber: sanitizedPhone,
        otp: agentPhoneOtp,
      });
      setAgentPhoneVerificationToken(response.data?.verificationToken || "");
      setAgentPhoneOtpVerified(Boolean(response.data?.phoneVerified));
      setAgentPhoneOtpMessage(
        response.message || "Phone number verified successfully."
      );
    } catch (err) {
      setAgentPhoneOtpError(
        err.response?.data?.message ||
          "We couldn't verify that code. Please try again."
      );
    } finally {
      setAgentPhoneOtpVerifying(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Register as Agent
        </h1>
        <p className="text-gray-600">
          Become a collection agent and help validate materials for sellers.
          Earn commissions on each delivery.
        </p>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes("successful")
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          <div className="flex items-center">
            {message.includes("successful") ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            {message}
          </div>
        </div>
      )}

      {/* Registration Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Verification Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Verification Method
              </label>
              <select
                name="verificationMethod"
                required
                value={formData.verificationMethod}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Select method</option>
                <option value="NIN">NIN</option>
                <option value="Voters card">Voters Card</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Verification Number
              </label>
              <input
                type="text"
                name="verificationNumber"
                required
                value={formData.verificationNumber}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter verification number"
              />
            </div>
          </div>

          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agent Name
              </label>
              <input
                type="text"
                name="agent_name"
                required
                value={formData.agent_name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Your full name"
              />
            </div>
          </div>

          {/* Location Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <StateSelector
                value={formData.state}
                onChange={(state) => setFormData({ ...formData, state })}
                placeholder="Select your state"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Local Government
              </label>
              <input
                type="text"
                name="localGovernmentAddress"
                required
                value={formData.localGovernmentAddress}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Your local government"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Street Address
            </label>
            <input
              type="text"
              name="streetAddress"
              required
              value={formData.streetAddress}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Your street address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <div className="mt-1 flex flex-col space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                <div className="flex w-full sm:w-auto">
                  <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500">
                    +234
                  </span>
                  <input
                    type="text"
                    name="phone_number"
                    required
                    value={formData.phone_number}
                    onChange={handleChange}
                    className="flex-1 min-w-0 appearance-none rounded-r-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    placeholder="812 345 6789"
                    maxLength={10}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSendAgentPhoneOtp}
                  disabled={
                    agentPhoneOtpSending || !formatPhoneNumberForApi(formData.phone_number)
                  }
                  className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {agentPhoneOtpSending
                    ? "Sending..."
                    : agentPhoneOtpSent
                    ? "Resend code"
                    : "Send code"}
                </button>
              </div>

              <p className="text-xs text-gray-500">
                Enter your Nigerian phone number without the leading zero. It must be exactly{" "}
                <span className="font-semibold text-gray-700">10 digits</span> and we'll format it as{" "}
                <span className="font-semibold text-gray-700">234XXXXXXXXXX</span> for submission.
              </p>

              {agentPhoneOtpSent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enter the 6-digit code
                  </label>
                  <div className="mt-1 flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      pattern="[0-9]{6}"
                      placeholder="••••••"
                      className="flex-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 tracking-widest sm:text-sm"
                      value={agentPhoneOtp}
                      onChange={(e) =>
                        setAgentPhoneOtp(e.target.value.replace(/\D/g, ""))
                      }
                      disabled={agentPhoneOtpVerified}
                    />
                    <button
                      type="button"
                      onClick={handleVerifyAgentPhoneOtp}
                      disabled={
                        agentPhoneOtpVerifying ||
                        agentPhoneOtp.length !== 6 ||
                        agentPhoneOtpVerified
                      }
                      className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400"
                    >
                      {agentPhoneOtpVerified
                        ? "Verified"
                        : agentPhoneOtpVerifying
                        ? "Verifying..."
                        : "Verify code"}
                    </button>
                  </div>
                </div>
              )}

              {agentPhoneOtpError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {agentPhoneOtpError}
                </div>
              )}

              {agentPhoneOtpMessage && (
                <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                  {agentPhoneOtpMessage}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Profile Image URL (optional)
            </label>
            <input
              name="agentImage"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="https://example.com/image.jpg"
            />
          </div>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Selected preview"
              className="mt-4 max-w-full h-auto rounded-md"
            />
          )}

          {/* Operating Hours */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Operating Hours
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(formData.openHours).map(([day, hours]) => (
                <div key={day}>
                  <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                    {day}
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="time"
                      value={hours.open}
                      onChange={(e) =>
                        handleOpenHoursChange(day, "open", e.target.value)
                      }
                      className="w-1/2 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Open"
                    />
                    <input
                      type="time"
                      value={hours.close}
                      onChange={(e) =>
                        handleOpenHoursChange(day, "close", e.target.value)
                      }
                      className="w-1/2 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Close"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-md transition-colors"
            >
              {loading ? "Registering..." : "Register as Agent"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-md transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">
          Agent Benefits
        </h3>
        <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
          <li>Earn commissions on each material delivery (₦20 per kg)</li>
          <li>Flexible working hours and location</li>
          <li>Professional validation tools and training</li>
          <li>Secure payment processing</li>
          <li>Build your reputation and ratings</li>
        </ul>
      </div>
    </div>
  );
};

export default RegisterAgent;
